import {
  DemandAiReviewResultItemDetailDto,
  DemandAiReviewResultItemGroupDto,
} from '#services/api';
import { Button, ImageViewer, Modal } from '@bedrock/components';
import { ImageConfig } from '@bedrock/components/lib/ImageViewer/ImageViewer';
import { ModalProps } from '@bedrock/components/lib/Modal';
import { sleep } from '@bedrock/components/lib/utils';
import { ProTablePlus } from '@bedrock/eed-react';
import { IProTableRef } from '@bedrock/eed-react/lib/components/ProTable/interface';
import {
  ThumbsDown,
  ThumbsDownFilled,
  ThumbsUp,
  ThumbsUpFilled,
} from '@bedrock/icons-react';
import { $sType } from '@mpc/common';
import { EllipsisTooltip } from '@mpc/components';
import { useMemoizedFn } from 'ahooks';
import classNames from 'classnames';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
} from 'react';
import './index.less';

// content

interface FileExceptionDetailContentProps {
  data?: DemandAiReviewResultItemGroupDto;
}

const FileExceptionDetailContent: React.FC<FileExceptionDetailContentProps> = ({
  data,
}) => {
  const [images, setImages] = useState<ImageConfig[]>([]);
  const [current, setCurrent] = useState(-1);

  const tableRef =
    React.useRef<IProTableRef<DemandAiReviewResultItemDetailDto>>(null);

  const queryService = useMemoizedFn(async () => {
    return {
      data: {
        list: [...(data?.details || [])],
        total: data?.details?.length || 0,
      },
    };
  });

  const onOpenLinkUrl = useMemoizedFn((url: string) => {
    window.open(url);
  });

  const onOpenImage = useMemoizedFn((url: string) => {
    setImages([{ url }]);
    setCurrent(0);
  });

  const onFeedbackChange = useMemoizedFn((index: number, feedback: number) => {
    const nextData = [...(tableRef.current.getData() || [])];
    nextData[index] = { ...nextData[index], feedback };
    tableRef.current.updateData(nextData);
  });

  return (
    <>
      <ProTablePlus
        ref={tableRef}
        columns={[
          {
            dataIndex: 'index',
            title: '#',
            width: 64,
            render: (v, r, i) => {
              return `${i + 1}`;
            },
          },
          {
            dataIndex: 'item_name',
            title: '异常类型',
            width: 200,
            render: (v) => {
              if (!v) return '-';
              return <EllipsisTooltip clamp={3} text={v} />;
            },
          },
          {
            dataIndex: 'content',
            title: '检测对象名称',
            width: 120,
            hide: $sType === 'outer',
            render: (v) => {
              if (!v) return '-';
              return <EllipsisTooltip clamp={3} text={v} />;
            },
          },
          {
            dataIndex: 'link_url',
            title: '检测内容',
            width: 200,
            render: (v, r) => {
              if (!v && !r.text) return '-';
              if (r.is_image) {
                return (
                  <EllipsisTooltip
                    clamp={3}
                    className="text-primary-1 cursor-pointer hover:text-primary-2"
                    text={'查看图片'}
                    onClick={() => onOpenImage(v)}
                  />
                );
              }
              if (r.is_link) {
                return (
                  <EllipsisTooltip
                    clamp={3}
                    className="text-primary-1 cursor-pointer hover:text-primary-2"
                    text={r.text || v}
                    onClick={() => onOpenLinkUrl(v)}
                  />
                );
              }
              return <EllipsisTooltip clamp={3} text={r.text || v} />;
            },
          },
          {
            dataIndex: 'error_message',
            title: '检测结果',
            hide: $sType === 'outer',
            render: (v, r) => {
              if (v) {
                return <EllipsisTooltip clamp={3} text={v} />;
              }
              return '-';
              // const { repeat_number, related_content } = r;
              // if (!repeat_number && !related_content) return '-';
              // return (
              //   <div>
              //     <EllipsisTooltip
              //       clamp={1}
              //       title={repeat_number}
              //       text={`重复单号：${repeat_number}`}
              //     />
              //     <EllipsisTooltip
              //       clamp={2}
              //       title={related_content}
              //       text={`相关内容：${related_content}`}
              //     />
              //   </div>
              // );
            },
          },
          {
            dataIndex: 'remark',
            title: '备注信息',
            render: (v, r) => {
              if (!v) return '-';
              return <EllipsisTooltip clamp={3} text={v} />;
            },
          },
          {
            dataIndex: 'feedback',
            title: '反馈',
            width: 76,
            hide: true,
            render: (v, r, i) => {
              return (
                <span className="inline-flex items-center gap-[8px]">
                  <Button
                    icon={v === 1 ? <ThumbsUpFilled /> : <ThumbsUp />}
                    size="xSmall"
                    type="text-subtle"
                    onClick={() => onFeedbackChange(i, v === 1 ? 0 : 1)}
                  />
                  <Button
                    icon={v === 2 ? <ThumbsDownFilled /> : <ThumbsDown />}
                    size="xSmall"
                    type="text-subtle"
                    onClick={() => onFeedbackChange(i, v === 2 ? 0 : 2)}
                  />
                </span>
              );
            },
          },
        ].filter((item) => !item.hide)}
        request={{ queryService }}
        rowKey="id"
        sticky
        pagination={false}
      />
      <ImageViewer
        images={images}
        current={current}
        onClose={() => setCurrent(-1)}
      />
    </>
  );
};

// modal

export interface FileExceptionDetailModalOpenParams {
  modalProps?: ModalProps;
  detailProps?: FileExceptionDetailContentProps;
}

export interface FileExceptionDetailModalRef {
  open?: (params?: FileExceptionDetailModalOpenParams) => void;
  close?: () => void;
}

const FileExceptionDetailModalComp: ForwardRefRenderFunction<
  FileExceptionDetailModalRef
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [openParams, setOpenParams] =
    useState<FileExceptionDetailModalOpenParams>(undefined);

  const open = useMemoizedFn((params?: FileExceptionDetailModalOpenParams) => {
    setOpenParams(params);
    setVisible(true);
  });

  const close = useMemoizedFn(async () => {
    setVisible(false);
    await sleep(300);
    setOpenParams(undefined);
  });

  useImperativeHandle(ref, () => ({ open, close }), [open, close]);

  return (
    <Modal
      okText="我知道了"
      title="异常明细"
      {...openParams?.modalProps}
      cancelButtonProps={{ style: { display: 'none' } }}
      className={classNames(
        'file-exception-detail-modal',
        openParams?.modalProps?.className
      )}
      destroyOnClose
      visible={visible}
      width={1200}
      onCancel={close}
      onOk={close}
    >
      <FileExceptionDetailContent {...openParams?.detailProps} />
    </Modal>
  );
};

const FileExceptionDetailModal = forwardRef(FileExceptionDetailModalComp);

export default FileExceptionDetailModal;
